import { Alert } from '@ksyos/design-system';
import { useState } from 'react';
import type { ICustomControlDiagnosticReportAddEditProps } from '../../interfaces';
import { BezoekAanSpecialistOption } from '../../../../../../common/enums/bezoekAanSpecialistOption';
import { ConclusieDiagnoseOption } from '../../../../../../common/enums/conclusieDiagnoseOption';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { ToastType } from '../../../../../../common/enums/toastType';
import { YesOrNo } from '../../../../../../common/enums/yesOrNo';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import {
    DermatologyPolicies,
    DermatologyUrgencies,
} from '../../../../../../common/validation/customControls/dermatology';
import { translateKey } from '../../../../../translate';
import { showServiceToast } from '../../../../../utils/showToast';
import { CancelButton } from '../../../../elements/carePathButtons/CancelButton';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import { ButtonContainer, Form, FormDivider, HeaderDivider } from '../../../../elements/common';
import { FormContextProvider } from '../../../../elements/formContext';
import { DropdownField } from '../../../../fields/Dropdown';
import { RadioGroupField } from '../../../../fields/RadioGroup';
import { TextAreaField } from '../../../../fields/TextArea';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { DiagnosisConclusionSelection } from '../../utils/v1/DiagnosisConclusionSelection';

export interface IDermaSnelCV1DiagnosticReportAddEditCustomControlData {
    beleid: DermatologyPolicies | null;
    beleidAndersText: string | null;
    bevindingen: string | null;
    bezoek_aan_specialist_nu_nodig: BezoekAanSpecialistOption | null;
    conclusie_diagnose: ConclusieDiagnoseOption;
    kwaliteit_fotos_voldoende: YesOrNo | null;
    omschrijving_beleid: string | null;
    toelichting: string | null;
    urgentie: DermatologyUrgencies | null;
}

const policyOptions = [
    DermatologyPolicies.expectatiefBeleid,
    DermatologyPolicies.verwijzingNaarDermatoloog,
    DermatologyPolicies.bioptDoorHuisarts,
    DermatologyPolicies.anders,
];

const policyOptionsZieBeleid = [
    DermatologyPolicies.verwijzingNaarDermatoloog,
    DermatologyPolicies.bioptDoorHuisarts,
    DermatologyPolicies.anders,
];

const urgencyOptions = [
    DermatologyUrgencies.sixMonths,
    DermatologyUrgencies.threeMonths,
    DermatologyUrgencies.oneMonth,
    DermatologyUrgencies.oneWeek,
    DermatologyUrgencies.oneDay,
];

export function DermaSnelCV1DiagnosticReportAddEdit(props: ICustomControlDiagnosticReportAddEditProps) {
    const [buttonDisabled, setButtonDisabled] = useState<boolean>();

    const customControlData: IDermaSnelCV1DiagnosticReportAddEditCustomControlData =
        props.diagnosticReport.customControlData;

    return (
        <ProcessContainer>
            <ProcessItemHeader
                date={props.process && props.process.dateSent}
                isFirstItem={props.isFirstItem}
                isItemOpen
                organizationName={props.currentDiagnosticReport?.performerOrganization?.name}
                practitionerName={
                    props.currentDiagnosticReport && props.currentDiagnosticReport.performerPractitioner
                        ? getDisplayName(props.currentDiagnosticReport.performerPractitioner)
                        : undefined
                }
                process={props.process}
                title={translateKey('diagnosticReport.category_consultReport')}
            />

            <Form onSubmit={() => null}>
                {addEditConsultReportForm()}

                <ButtonContainer
                    alignRight
                    buttons={[
                        <CancelButton
                            cancel={props.onCancel}
                            entity={ProcessEntity.diagnosticReport}
                            key="cancel"
                            serviceCode={props.service.code}
                            serviceType={props.service.serviceType}
                        />,
                        <SaveButton
                            buttonDisabled={buttonDisabled}
                            key="save"
                            save={async () => {
                                await saveDiagnosticReport('draft');
                            }}
                        />,
                        <SaveSendButton
                            buttonDisabled={buttonDisabled}
                            key="saveSend"
                            saveSend={async () => {
                                await saveDiagnosticReport('final');
                            }}
                        />,
                    ]}
                />
            </Form>
        </ProcessContainer>
    );

    function addEditConsultReportForm() {
        return (
            <>
                <FormContextProvider
                    value={{
                        errors: props.errors,
                        item: customControlData,
                        onChange: async (key, value) => await props.handleChangeCustomControl({ [key]: value }),
                        onDoValidate: async (key) => await props.validateOneCustomControl(key),
                        placeholderGroupName: 'diagnosticReport',
                    }}
                >
                    <TextAreaField
                        error={props.errors.get('customControlData.bevindingen')}
                        fieldKey="bevindingen"
                        hasErrors={props.errors.has('customControlData.bevindingen')}
                        isRequired
                        label={translateKey('diagnosticReport.findings')}
                        placeholder="Omschrijf je bevindingen..."
                    />

                    <RadioGroupField
                        error={props.errors.get('customControlData.kwaliteit_fotos_voldoende')}
                        fieldKey="kwaliteit_fotos_voldoende"
                        hasErrors={props.errors.has('customControlData.kwaliteit_fotos_voldoende')}
                        label={translateKey('diagnosticReport.qualityPhotosSufficient')}
                        labels={[translateKey('common.yes'), translateKey('common.no')]}
                        name="kwaliteit_fotos_voldoende"
                        options={[YesOrNo.yes, YesOrNo.no]}
                    />

                    <DiagnosisConclusionSelection
                        conclusie_diagnose={customControlData.conclusie_diagnose}
                        diagnosis={props.diagnosticReport.diagnosis}
                        errors={props.errors}
                        handleChangeConclusieDiagnose={props.handleChangeCustomControl}
                        handleChangeDiagnosisConsideration={async (diagnosis) =>
                            await props.handleChange('diagnosis', diagnosis)
                        }
                        isRequired
                        label="Heb je een werkdiagnose?"
                        service={props.service}
                        validateConclusieDiagnose={props.validateOneCustomControl}
                        validateDiagnosisConsideration={async () => props.validateOne('diagnosis')}
                    />

                    <FormDivider />
                    <HeaderDivider text={translateKey('diagnosticReport.advice')} />

                    <TextAreaField
                        error={props.errors.get('customControlData.omschrijving_beleid')}
                        fieldKey="omschrijving_beleid"
                        hasErrors={props.errors.has('customControlData.omschrijving_beleid')}
                        label={translateKey('diagnosticReport.policyDescription')}
                        placeholder="Een toelichting, opmerkingen of vragen..."
                    />

                    <DropdownField
                        automation="beleid"
                        error={props.errors.get('customControlData.beleid')}
                        fieldKey="beleid"
                        hasErrors={props.errors.has('customControlData.beleid')}
                        label={translateKey('diagnosticReport.orChoosePolicyFromList')}
                        onChange={async (value) => {
                            const update = { ...customControlData, beleid: value };
                            if (value === DermatologyPolicies.expectatiefBeleid) {
                                update.urgentie = null;
                            }
                            if (value !== DermatologyPolicies.anders) {
                                update.beleidAndersText = null;
                            }
                            if (
                                value === DermatologyPolicies.expectatiefBeleid ||
                                value === DermatologyPolicies.verwijzingNaarDermatoloog
                            ) {
                                update.bezoek_aan_specialist_nu_nodig = BezoekAanSpecialistOption.ja;
                            }
                            await props.handleChangeCustomControl(update);
                        }}
                        placeholder="Kies het te voeren beleid..."
                        selectOptions={generatePolicyOptions()}
                    />

                    {customControlData.beleid === DermatologyPolicies.anders && (
                        <TextAreaField
                            automation="beleidAndersText"
                            error={props.errors.get('customControlData.beleidAndersText')}
                            fieldKey="beleidAndersText"
                            hasErrors={props.errors.has('customControlData.beleidAndersText')}
                            label=" "
                            placeholder="Toelichting..."
                        />
                    )}

                    <RadioGroupField
                        error={props.errors.get('customControlData.bezoek_aan_specialist_nu_nodig')}
                        fieldKey="bezoek_aan_specialist_nu_nodig"
                        hasErrors={props.errors.has('customControlData.bezoek_aan_specialist_nu_nodig')}
                        isRequired
                        label={translateKey('diagnosticReport.visitToSpecialistNecessaryNow')}
                        name="bezoek_aan_specialist_nu_nodig"
                        onChange={async (value) => {
                            const update = { ...customControlData, bezoek_aan_specialist_nu_nodig: value };
                            if (value !== BezoekAanSpecialistOption.ja) {
                                update.urgentie = null;
                            }
                            if (
                                value === BezoekAanSpecialistOption.zieBeleid &&
                                customControlData.beleid === DermatologyPolicies.expectatiefBeleid
                            ) {
                                update.beleid = null;
                            }
                            await props.handleChangeCustomControl(update);
                        }}
                        options={[
                            BezoekAanSpecialistOption.ja,
                            BezoekAanSpecialistOption.nee,
                            BezoekAanSpecialistOption.zieBeleid,
                        ]}
                    />

                    {customControlData.bezoek_aan_specialist_nu_nodig === BezoekAanSpecialistOption.ja &&
                        customControlData.beleid !== DermatologyPolicies.expectatiefBeleid && (
                            <DropdownField
                                automation="urgentie"
                                error={props.errors.get('customControlData.urgentie')}
                                fieldKey="urgentie"
                                hasErrors={props.errors.has('customControlData.urgentie')}
                                label={translateKey('diagnosticReport.urgency')}
                                placeholder="Kies de mate van urgentie..."
                                selectOptions={urgencyOptions.map((value) => {
                                    return { text: value, value };
                                })}
                            />
                        )}

                    <TextAreaField
                        automation="toelichting"
                        error={props.errors.get('customControlData.toelichting')}
                        fieldKey="toelichting"
                        hasErrors={props.errors.has('customControlData.toelichting')}
                        label={translateKey('diagnosticReport.remarks')}
                        placeholder={translateKey('diagnosticReport.remarks')}
                    />

                    {customControlData.urgentie === DermatologyUrgencies.oneDay && (
                        <Alert appearance="info">
                            Let op: Neem bij spoed altijd contact op met Ksyos via 020 600 00 60 dan pakken wij de
                            spoedverwijzing op
                        </Alert>
                    )}
                </FormContextProvider>
            </>
        );
    }

    async function saveDiagnosticReport(saveType: 'draft' | 'final') {
        setButtonDisabled(true);

        const validatedDiagnosticReport = await props.validateDiagnosticReport(saveType);
        if (!validatedDiagnosticReport) {
            setButtonDisabled(false);

            return;
        }

        const diagnosticReportId = await props.onSaveDiagnosticReport(saveType);

        if (saveType === 'draft') {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                props.service,
                'diagnosticReport',
                translateKey('toast.draftDefaultMessage')
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.submitSuccess',
                props.service,
                'diagnosticReport',
                translateKey('toast.answerSentToOrganizationSuccess', props.request.requesterOrganization?.name)
            );
        }

        setButtonDisabled(false);
        await props.onSave(diagnosticReportId);
    }

    function generatePolicyOptions() {
        let currentPolicyOptions;
        if (customControlData.bezoek_aan_specialist_nu_nodig === BezoekAanSpecialistOption.zieBeleid) {
            currentPolicyOptions = policyOptionsZieBeleid;
        } else {
            currentPolicyOptions = policyOptions;
        }

        return currentPolicyOptions.map((value) => {
            return { text: value, value };
        });
    }
}

export function dermaSnelCV1DiagnosticReportInitializeCustomControlData() {
    return {
        beleid: null,
        beleidAndersText: null,
        bevindingen: null,
        bezoek_aan_specialist_nu_nodig: null,
        conclusie_diagnose: ConclusieDiagnoseOption.ja,
        kwaliteit_fotos_voldoende: null,
        omschrijving_beleid: null,
        toelichting: null,
        urgentie: null,
    };
}
